import { Link } from 'react-router-dom'
import { Facebook, Linkedin, Phone, Mail, MapPin, Cookie, FileText } from 'lucide-react'
import { companyInfo } from '@/data/company'
import { footerNav } from '@/data/navigation'
import { openCookieConsent } from '@/components/CookieConsentBanner'

export function Footer() {
  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-primary-600/50 border-t border-white/10">
      {/* Main footer */}
      <div className="container-custom section-padding">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Company info */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/logo.png" 
                alt="UC Advisors" 
                className="h-12 md:h-14 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-sm mb-6">
              Optimize your business with Technology. Your strategic partner for UCaaS, CCaaS, Network, SD-WAN, and Cybersecurity solutions.
            </p>
            <div className="flex gap-4">
              <a
                href={companyInfo.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-slate-400 hover:bg-accent-500/20 hover:text-accent-400 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href={companyInfo.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-slate-400 hover:bg-accent-500/20 hover:text-accent-400 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">Solutions</h4>
            <ul className="space-y-3">
              {footerNav.solutions.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-accent-400 transition-colors text-sm"
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Free Assessments */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">Free Assessments</h4>
            <ul className="space-y-3">
              {footerNav.assessments.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-accent-400 transition-colors text-sm"
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries & Company */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">Industries</h4>
            <ul className="space-y-3 mb-8">
              {footerNav.industries.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-accent-400 transition-colors text-sm"
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-heading font-semibold text-white mb-6">Company</h4>
            <ul className="space-y-3">
              {footerNav.company.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-accent-400 transition-colors text-sm"
                  >
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${companyInfo.phoneRaw}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-accent-400 transition-colors text-sm group"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-accent-500/20 transition-colors">
                    <Phone className="w-5 h-5" />
                  </div>
                  {companyInfo.phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-3 text-slate-400 hover:text-accent-400 transition-colors text-sm group"
                >
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-accent-500/20 transition-colors">
                    <Mail className="w-5 h-5" />
                  </div>
                  {companyInfo.email}
                </a>
              </li>
              {companyInfo.locations.map((location) => (
                <li key={location.city} className="flex items-start gap-3 text-slate-400 text-sm">
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-medium text-slate-300">{location.city}, {location.state}</div>
                    <div>{location.address}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <p>© {currentYear}, UC Advisors | All Rights Reserved</p>
            <div className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-2">
              <Link to="/contact" className="hover:text-accent-400 transition-colors flex items-center gap-1">
                <FileText className="w-4 h-4" />
                Get Free Quote
              </Link>
              <Link to="/terms" className="hover:text-accent-400 transition-colors">
                Terms & Conditions
              </Link>
              <Link to="/privacy" className="hover:text-accent-400 transition-colors">
                Privacy Policy
              </Link>
              <Link to="/cookies" className="hover:text-accent-400 transition-colors">
                Cookie Policy
              </Link>
              <button 
                onClick={openCookieConsent}
                className="hover:text-accent-400 transition-colors flex items-center gap-1"
              >
                <Cookie className="w-4 h-4" />
                Cookie Preferences
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

